/**
 * The action modal (hold / deviation / return) as one component: a titled dialog whose body is the caller's fields,
 * a Cancel and a Confirm. It carries no rules of its own — the caller decides when Confirm is enabled and the server
 * decides whether the act is allowed; a refusal is shown inside the dialog in the server's words and the dialog stays
 * open, so nothing the person typed is lost.
 *
 * There is no password or PIN field: a signature is never taken here (ESIG-008).
 */
import { type ReactNode, useEffect, useRef } from "react";
import { useDialogFocus } from "../lib/dialog-focus.js";
import { REFUSAL } from "./stitch-review-ui.js";
import { Button, Icon } from "./ui/index.js";

/** amber and red are the cautionary dialogs (confirm is the danger button); navy is a plain decision. */
const TONES = {
  amber: { icon: "text-warn", confirm: "danger" },
  red: { icon: "text-alarm", confirm: "primary" },
  navy: { icon: "text-brand", confirm: "primary" },
} as const;

export interface ActionDialogProps {
  title: string;
  icon: string;
  tone?: keyof typeof TONES;
  confirmLabel: string;
  confirmDisabled?: boolean;
  pending?: boolean;
  refusal?: string | null;
  onConfirm: () => void;
  onClose: () => void;
  children: ReactNode;
}

export function ActionDialog(p: ActionDialogProps) {
  const tone = TONES[p.tone ?? "navy"];
  const { onClose } = p;
  const box = useRef<HTMLDivElement>(null);
  useDialogFocus(box);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[var(--elevation-modalBackdrop)]">
      <div
        ref={box}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={p.title}
        className="mx-4 w-full max-w-lg overflow-hidden rounded-lg border border-line bg-surface shadow-[var(--elevation-level2)]"
      >
        <header className="flex items-center justify-between border-b border-line px-5 py-3">
          <div className="flex items-center gap-2">
            <span className={tone.icon}>
              <Icon name={p.icon} size={20} />
            </span>
            <h3 className="m-0 text-base font-semibold text-ink">{p.title}</h3>
          </div>
          <button
            type="button"
            aria-label="Close"
            className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-transparent text-ink-muted hover:bg-subtle"
            onClick={p.onClose}
          >
            <Icon name="close" size={20} />
          </button>
        </header>
        <div className="space-y-4 p-5 text-sm text-ink">
          {p.refusal ? (
            <p role="alert" className={REFUSAL}>
              {p.refusal}
            </p>
          ) : null}
          {p.children}
        </div>
        <footer className="flex justify-end gap-2 border-t border-line px-5 py-3">
          <Button variant="secondary" onClick={p.onClose}>
            Cancel
          </Button>
          <Button variant={tone.confirm} disabled={p.confirmDisabled || p.pending} onClick={p.onConfirm}>
            {p.confirmLabel}
          </Button>
        </footer>
      </div>
    </div>
  );
}

export const DIALOG_LABEL = "block text-sm font-medium text-ink mb-1";
export const DIALOG_FIELD =
  "block w-full min-h-9 rounded-md border border-line-strong bg-surface px-3 py-1.5 text-sm text-ink focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-1 focus-visible:outline-brand";
